import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { ReportingMutationTypes } from './mutation-types';
import { ReportingActionTypes } from './action-types';
import {
  ReportingActivityApiResponse,
  ReportingTableApiOptions,
  ReportingTableApiResponse,
  ReportingActivityApiOptions,
} from './types';

import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [ReportingActionTypes.GET_REPORTING_TABLE](
    { commit }: AugmentedAction,
    payload: ReportingTableApiOptions
  ): Promise<void>;
  [ReportingActionTypes.GET_REPORTING_ACTIVITY](
    { commit }: AugmentedAction,
    payload: ReportingActivityApiOptions
  ): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [ReportingActionTypes.GET_REPORTING_TABLE](
    { commit },
    reportingTableApiOptions: ReportingTableApiOptions
  ) {
    try {
      commit(ReportingMutationTypes.CLEAR_REPORTING_TABLE, undefined);

      const paramString = combineQueryParams({
        ['date_after']: reportingTableApiOptions.dateAfter,
        ['date_before']: reportingTableApiOptions.dateBefore,
        ['sort_by']: reportingTableApiOptions.sortBy,
        ['sort_descending']: reportingTableApiOptions.sortDescending,
        ['group_by']: reportingTableApiOptions.groupBy,
        filters: reportingTableApiOptions.filters,
      });

      const response: ReportingTableApiResponse = await ServerAPI.get(
        `/reporting/opportunities${paramString}`
      );

      commit(ReportingMutationTypes.SET_REPORTING_TABLE, response);
    } catch (e) {
      handleError(e);
      commit(
        ReportingMutationTypes.SET_REPORTING_TABLE,
        {} as ReportingTableApiResponse
      );
    }
  },

  async [ReportingActionTypes.GET_REPORTING_ACTIVITY](
    { commit },
    reportingActivityApiOptions: ReportingActivityApiOptions
  ) {
    try {
      const paramString = combineQueryParams({
        filters: reportingActivityApiOptions.filters,
      });

      const response: ReportingActivityApiResponse = await ServerAPI.get(
        `/reporting/activity${paramString}`
      );

      commit(ReportingMutationTypes.SET_REPORTING_ACTIVITY, response);
    } catch (e) {
      handleError(e);
      commit(
        ReportingMutationTypes.SET_REPORTING_ACTIVITY,
        {} as ReportingActivityApiResponse
      );
    }
  },
};
